/**
 * Shared context for everything the LayoutRenderer hands to primitives.
 *
 * Primitives are pure-ish components driven by their `config` (per-instance
 * overrides from the layout JSON) plus context (real data — event metadata,
 * the LiveKit room, the camera list, the currently-watched camera). This
 * context is the second.
 */

import { createContext, useContext, type ReactNode } from 'react'
import type { Room } from 'livekit-client'

/** What every primitive needs to know about the event it's rendering. */
export interface LayoutEvent {
  id: string
  name: string
  subtitle: string | null
  description: string | null
  starts_at: string | null
  ends_at: string | null
  template_key: string
  featured_participant_identity: string | null
  organization: {
    id: string
    name: string
    display_name: string | null
    description: string | null
    logo_url: string | null
    verified: boolean
    /** Number of events this org has streamed. Used as the producer-row stat. */
    events_count: number
  }
  /** The producer-published route, when parsed. Null in every other route_parse_status. */
  route: {
    id: string | null
    distance_km: number | null
    elevation_gain_m: number | null
    elevation_loss_m: number | null
    waypoint_count: number | null
    centroid_lat: number | null
    centroid_lng: number | null
    bounds: { north: number; south: number; east: number; west: number } | null
    gpx_url: string | null
  } | null
}

/** A camera attached to the event. */
export interface LayoutCamera {
  participant_identity: string
  name: string
  position_km: number | null
  online: boolean
  public_audio_enabled: boolean
  /**
   * Optional fallback image when no LiveKit track is available — demo mode,
   * pre-event teasers, or simply a producer-uploaded still frame. Primitives
   * prefer the live track when one is publishing.
   */
  preview_image_url?: string | null
  /**
   * Geo coordinates for map placement. Null when the camera is unpinned
   * (RouteMap simply skips it). Sourced from stream pinned_lat/lng or fresh
   * telemetry, whichever is more recent.
   */
  latitude?: number | null
  longitude?: number | null
}

/** Local viewer state — the override pattern from EVENT_AUTHORING_SPEC §3.2. */
export interface ViewerSelection {
  /** Currently-watched identity. `featuredFromRoom` unless the viewer overrode. */
  watchingIdentity: string | null
  /** Whether the viewer has manually selected away from the featured camera. */
  hasOverride: boolean
  /** Manually select a camera. */
  selectCamera: (identity: string) => void
  /** Snap back to whatever the producer's featured camera currently is. */
  followProducer: () => void
}

export interface LayoutContextValue {
  event: LayoutEvent
  cameras: LayoutCamera[]
  /**
   * The LiveKit room — strictly the real LiveKit Room instance, or null.
   * Primitives that mount LiveKit hooks (StreamVideoSurface, useTracks) MUST
   * be guarded behind `room` truthiness. Demo / preview mode passes null.
   */
  room: Room | null
  /**
   * Live viewer count. Read from `room.numParticipants` in production; demo
   * mode supplies a synthetic value. Null hides the chip.
   */
  viewerCount: number | null
  /** Featured identity from `roomMetadataChanged`. May lag the events column by ~100ms. */
  featuredFromRoom: string | null
  selection: ViewerSelection
}

const LayoutCtx = createContext<LayoutContextValue | null>(null)

export function LayoutProvider({
  value,
  children,
}: {
  value: LayoutContextValue
  children: ReactNode
}) {
  return <LayoutCtx.Provider value={value}>{children}</LayoutCtx.Provider>
}

/**
 * Primitives call this to read shared state.
 * Throws if used outside a LayoutProvider — the renderer always wraps.
 */
export function useLayoutContext(): LayoutContextValue {
  const ctx = useContext(LayoutCtx)
  if (!ctx) {
    throw new Error(
      'useLayoutContext must be used inside <LayoutProvider> — the renderer always wraps every primitive.',
    )
  }
  return ctx
}
